import React, { Component } from 'react';
import PropTypes from 'prop-types';
import WebFont from 'webfontloader';
import CacheBuster from 'react-cache-buster';
import cn from 'classnames';

import { observer, inject } from 'mobx-react';
import { Redirect } from 'react-router-dom';
import { configure } from 'react-hotkeys';

import { Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import SkeletonLoader from '@common/components/SkeletonLoader';
import config from '@common/config';

import Header from '../components/core/Header/Header';
import ErrorBoundary from '../components/error';

import AuthenticatedRouteContainer from './AuthenticatedRouteContainer';

import { version } from '../../../package.json';

import 'react-vis/dist/style.css';

// To detect subnodes of hotkey combinations.
configure({
  allowCombinationSubmatches: true,
  ignoreTags: ['input'],
  ignoreEventsCondition() { },
});

WebFont.load({
  google: {
    families: ['Public Sans:300,400,500,600'],
  },
});

class App extends Component {
  async componentDidMount() {
    const {
      userStore,
      onboardingStore,
      inboxStore,
    } = this.props;

    if (config.isAcademy && !userStore.user) {
      await userStore.academyLoginByToken();
    }

    onboardingStore.fetchWelcomePlacemat();

    const exclusionPath = ['/invitations', '/login', 'onboarding'];

    if (window.location.pathname !== '/') {
      onboardingStore.finishFirstMount();
    }

    if (exclusionPath.every((pathToCheck) => window.location.pathname.indexOf(pathToCheck) !== 0)) {
      if (!userStore.user) {
        localStorage.setItem('relayState', window.location.pathname);
        userStore.fetchUserProfile();
      }

      inboxStore.getInboxNotificationData();
    } else {
      userStore.setUserProfile({ language: 'en' });
    }
  }

  render() {
    const {
      location: {
        pathname,
      },
    } = this.props;

    const {
      match,
      history,
      location,
      userStore: {
        isFetchingUserProfile,
        hasCompletedOnboarding,
        user: currentUser,
        showingHeader,
      },
      notificationsStore: {
        notifications,
      },
    } = this.props;

    const isProduction = process.env.NODE_ENV === 'production';

    if (config.gaId && currentUser) {
      const { gtag } = window;

      if (gtag) {
        gtag('config', config.gaId, {
          user_id: currentUser.username,
        });
      }
    }

    if (isFetchingUserProfile) {
      return <SkeletonLoader />;
    }

    if (!isFetchingUserProfile && pathname !== '/onboarding' && !hasCompletedOnboarding) {
      return <Redirect to={{ pathname: '/onboarding', state: { from: location } }} />;
    }

    const customSiteTitle = currentUser?.customerTheme?.siteTitleText;
    if (customSiteTitle) {
      document.title = customSiteTitle;
    }

    return (
      <ErrorBoundary>
        <CacheBuster
          currentVersion={version}
          isEnabled={isProduction}
          isVerboseMode={false}
          loadingComponent={<SkeletonLoader />}
        >
          <div className={cn({ 'h-full': pathname === '/onboarding' })}>
            <Header hidden={!showingHeader || isFetchingUserProfile} />
            <AuthenticatedRouteContainer
              history={history}
              match={match}
              location={location}
            />
          </div>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            style={{ zIndex: 9999 }}
            open={notifications.length > 0}
            autoHideDuration={5000}
            ContentProps={{
              'aria-describedby': 'message-id',
            }}
          >
            {(notifications.length > 0 && notifications[0].label) && (
              <Alert severity={notifications[0].severity}>
                {notifications[0].label}
              </Alert>
            )}
          </Snackbar>
        </CacheBuster>
      </ErrorBoundary>
    );
  }
}

App.propTypes = {
  userStore: PropTypes.shape({
    isFetchingUserProfile: PropTypes.bool.isRequired,
    hasCompletedOnboarding: PropTypes.bool.isRequired,
    fetchUserProfile: PropTypes.func.isRequired,
    setUserProfile: PropTypes.func.isRequired,
    getUsers: PropTypes.func.isRequired,
    getUserAvatars: PropTypes.func.isRequired,
    academyLoginByToken: PropTypes.func.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string,
      username: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      customerId: PropTypes.string,
      role: PropTypes.string,
      customerTheme: PropTypes.shape({
        siteTitleText: PropTypes.string,
      }),
    }),
    showingHeader: PropTypes.bool.isRequired,
  }).isRequired,
  inboxStore: PropTypes.shape({
    getInboxNotificationData: PropTypes.func.isRequired,
  }).isRequired,
  onboardingStore: PropTypes.shape({
    fetchWelcomePlacemat: PropTypes.func.isRequired,
    finishFirstMount: PropTypes.func.isRequired,
  }).isRequired,
  notificationsStore: PropTypes.shape({
    notifications: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        severity: PropTypes.string,
      })),
      PropTypes.object,
    ]).isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};

export default inject('userStore', 'inboxStore', 'notificationsStore', 'onboardingStore')(observer(App));
